'use client';

import React, { useState, useEffect, useId } from 'react';
import { getMarketTodayData, getIndicesDetails } from '../../services/TradingMallService';
import { getSessionData } from '@repo/shared-utils/common-utils';
import { selectIsAuthSuccessOrPerpetual, useAppSelector } from '@repo/web-store';
import type { MarketTodayApiResponse, MarketTodayProps } from '../../interfaces/MarketToday.interface';
import MarketTodayActions from './MarketTodayActions';
import MarketIndicesList from './MarketIndicesList';
import BFLLink from '@repo/shared-ui/BFLLink';
import BFLSkeleton from '@repo/shared-ui/BFLSkeleton';
import { componentNameConstants, dlElementConstants, dlEventConstants } from '@repo/analytics/data-layer.constants';
import { ApiIndexData } from '../../interfaces/MarketToday.interface';
import { dataLayer } from '@repo/analytics/data-layer-utils';
import NoResultsFound from '../NoResultsFound';

const hasValidMarketTodayData = (value: unknown): value is MarketTodayApiResponse =>
  !!value && typeof value === 'object' && Array.isArray((value as MarketTodayApiResponse).indicesList);

const getDefaultMarketTodayData = (entity: string): Promise<MarketTodayApiResponse> =>
  getMarketTodayData(entity);

const getLoggedInMarketTodayData = async (entity: string): Promise<MarketTodayApiResponse> => {
  try {
    const { mobileNo } = await getSessionData();
    if (!mobileNo) return getDefaultMarketTodayData(entity);

    const indicesData = await getIndicesDetails({ mobileNo });
    return hasValidMarketTodayData(indicesData) && indicesData.indicesList.length > 0
      ? indicesData
      : getDefaultMarketTodayData(entity);
  } catch {
    return getDefaultMarketTodayData(entity);
  }
};

const MarketToday: React.FC<MarketTodayProps> = ({ content, initialData, initialModalData, initialHasError = false }) => {
  const [data, setData] = useState<MarketTodayApiResponse | null>(initialData || null);
  const [dataModal, setDataModal] = useState<MarketTodayApiResponse | null>(initialModalData || null);
  const [hasError, setHasError] = useState(initialHasError);
  const skeletonId = useId();
  const isLoggedIn = useAppSelector(selectIsAuthSuccessOrPerpetual);

  const fetchData = async () => {
    try {
      setHasError(false);
      // For modal data, always use getMarketTodayData (for filter options)
      const modalDataPromise = getMarketTodayData(content.marketTodayOptions!);
      // For main display data, use getIndicesDetails if user is logged in, otherwise use getMarketTodayData
      const marketDataPromise = isLoggedIn
        ? getLoggedInMarketTodayData(content.marketToday!)
        : getDefaultMarketTodayData(content.marketToday!);

      const [marketData, modalData] = await Promise.all([
        marketDataPromise,
        modalDataPromise
      ]);

      if (!hasValidMarketTodayData(marketData) || !hasValidMarketTodayData(modalData)) {
        setHasError(true);
        return;
      }

      setData(marketData);
      setDataModal(modalData);
    } catch (error) {
      console.error('Error fetching market data:', error);
      setHasError(true);
    }
  };

  useEffect(() => {
    // If user logs in, always fetch to get personalized data
    // If we don't have initial data, fetch it
    // If we have initial data and user is not logged in, skip fetch (use SSR data)
    if (!initialHasError && (isLoggedIn || !initialData || !initialModalData)) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const handleDataRefresh = async () => {
    await fetchData();
  };

  if (hasError) {
    return (
      <div className="market-container w-full py-3 px-4">
        <NoResultsFound />
      </div>
    );
  }

  if (!data || !dataModal) {
    return (
      <div className="market-container w-full py-3 px-4">
        <div className="flex items-center gap-3 md:gap-3 justify-between md:justify-start mb-3">
          <h2 className="font-medium text-[#1a1a1a] text-[20px] min-h-[24px]">{content?.componentTitle}</h2>
        </div>
        {/* Skeleton loader for nifty tiles */}
        <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 mb-3">
          {Array.from({ length: 5 }, (_, index) => (
            <div
              key={`${skeletonId}-skeleton-${index}`}
              className="flex-shrink-0 w-[138px] md:w-[160px] bg-white rounded-[8px] p-2 border border-[#F2F2F2] h-[86px] md:h-[97px] flex flex-col"
            >
              <BFLSkeleton height={19} classes="mb-3 md:mb-4" />
              <BFLSkeleton height={20} classes="mb-1" />
              <BFLSkeleton height={16} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const handleCardClick = (e: React.MouseEvent<HTMLElement>, stockItem: ApiIndexData) => {
    const url = window.location?.href?.includes("stock-and-trading");
    const dataLayerObj = {
      section_title: content?.componentTitle ?? componentNameConstants.MARKET_TODAY,
      component: componentNameConstants.MARKET_TODAY,   // double checked
      event: dlEventConstants.CLICK,
      element: dlElementConstants.VIEW_ALL_CLICK,
      cta_text: componentNameConstants.VIEW_MARKETS,
      event_type: dlEventConstants.CARD_CLICK,
      destination_url: stockItem.redirectURL,
    };
    const cleverTapObject = {
      EP_PAGE_NAME: dlElementConstants.TRADING_MALL_CLP,
      EP_TILE_CLICKED: stockItem?.fullName ?? "",
      EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
      // EVENT_NAME: url ? dlElementConstants.CLP_CLICK : dlElementConstants.PLP_CLICKED,
    }
    dataLayer("", dataLayerObj, cleverTapObject);
  }

  const dlData = {
    section_title: content?.componentTitle ?? componentNameConstants.SHARE_MARKET_TODAY,
    component: componentNameConstants.MARKET_TODAY,
    event: dlEventConstants.CLICK,
    element: dlElementConstants.VIEW_ALL_CLICK,
    cta_text: dlElementConstants.VIEW_MARKETS,
    event_type: dlEventConstants.BUTTON_CLICK,
    destination_url: content?.url ?? "",
  };

  const cleverTapObj = {
    EP_PAGE_NAME: dlElementConstants.TRADING_MALL_CLP,
    EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
    EP_CTA: dlElementConstants.VIEW_MARKETS,
    EP_TILE_CLICKED: ""
  };

  return (
    <div className="market-container w-full py-3 px-4">
      <div className="flex items-center gap-3 md:gap-3 justify-between md:justify-start mb-3">
        <h2 className="font-medium text-[#1a1a1a] text-[20px] min-h-[24px]">{content?.componentTitle}</h2>
        <MarketTodayActions modalData={dataModal} stockData={data} content={content} onDataRefresh={handleDataRefresh} />
      </div>
      <MarketIndicesList data={data} handleClick={handleCardClick} />
      <BFLLink openInNewTab href={content?.url} ctProps={JSON.stringify(cleverTapObj)} dlProps={JSON.stringify(dlData)}>
        <div className="max-w-fit cursor-pointer border-0 bg-transparent px-0 text-left transition-opacity duration-200 ease-linear">
          <button
            type="button"
            className="text-link-primary cursor-pointer h-[18px] font-[500]"
          >
            {content?.details}
          </button>
        </div>
      </BFLLink>
    </div>
  );
};

export default MarketToday;
